import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

interface PendingContract {
  id: string;
  template_name: string | null;
  signed_at: string;
  client_id: string | null;
  clients: { first_name: string | null; last_name: string | null } | null;
}

export function NotificationBell() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const { data: pendingContracts = [] } = useQuery({
    queryKey: ['pending-cosign'],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('signed_contracts')
        .select('id, template_name, signed_at, client_id, clients(first_name, last_name)')
        .eq('owner_id', session?.user?.id)
        .eq('status', 'pending_practitioner')
        .order('signed_at', { ascending: false });
      return (data ?? []) as PendingContract[];
    },
    refetchInterval: 30_000,
    enabled: !!session?.user?.id,
  });

  const count = pendingContracts.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          aria-label={`${count} consentement${count !== 1 ? 's' : ''} en attente`}
        >
          <Bell size={18} />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center leading-none">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80" align="end" sideOffset={6}>
        <div className="px-4 py-3 border-b">
          <p className="text-sm font-semibold">Contresignatures en attente</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {count === 0
              ? 'Aucun consentement en attente'
              : `${count} consentement${count > 1 ? 's' : ''} à contresigner`}
          </p>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {count === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Tout est à jour ✓
            </div>
          ) : (
            pendingContracts.map(contract => {
              const clientName = contract.clients
                ? [contract.clients.first_name, contract.clients.last_name].filter(Boolean).join(' ')
                : null;
              return (
                <button
                  key={contract.id}
                  onClick={() => navigate(`/clients/${contract.client_id}?tab=documents&cosign=${contract.id}`)}
                  className="w-full px-4 py-3 flex items-start gap-3 hover:bg-secondary/50 transition-colors text-left border-b last:border-0"
                >
                  <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {contract.template_name || 'Consentement'}
                    </p>
                    {clientName && (
                      <p className="text-xs text-muted-foreground truncate">{clientName}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {new Date(contract.signed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <span className="text-xs text-amber-600 font-semibold shrink-0 mt-0.5">Contresigner</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
